import { motion } from 'framer-motion';
import { User, ShoppingCart, Settings } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import CheckoutModal from '@/pages/Budget/Checkout';

import { useMandatoryAuthBlocking } from '@/components/AuthGate';
import {
  fetchAuthMe,
  getStaffEntryPath,
  isInternalDisabledApiError,
  isStaffUser,
  resolveHasStaffAccess,
} from '@/services/authSession';
import ForgotPasswordModal from '../ForgotPasswordModal';
import LoginModal from '../LoginModal';
import ProfileModal from '../ProfileModal';
import RegisterModal from '../RegisterModal';

import './style.css';

const Header = () => {
  const navigate = useNavigate();
  const mandatoryAuthBlocking = useMandatoryAuthBlocking();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [hideCheckoutClose, setHideCheckoutClose] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [cartEventCount, setCartEventCount] = useState(0);

  const refreshStaffAccess = useCallback(async () => {
    setIsStaff(await resolveHasStaffAccess({ preserveSession: true }));
  }, []);

  // Verificar se o usuário está logado
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('authToken');
      const loggedIn = !!token;
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        void refreshStaffAccess();
      } else {
        setIsStaff(false);
      }
    };
    
    // Verificar status inicial
    checkLoginStatus();
    
    // Listener para quando login é feito (incluindo após registro)
    const userLoggedInListener = () => {
      checkLoginStatus();
    };
    
    // Listener para quando precisa abrir login
    const openLoginListener = () => {
      setIsLoggedIn(false);
      setIsLoginOpen(true);
    };
    
    window.addEventListener('user-logged-in', userLoggedInListener as EventListener);
    window.addEventListener('open-login', openLoginListener as EventListener);

    const onAuthExpired = () => {
      setIsStaff(false);
      setIsLoggedIn(false);
    };
    window.addEventListener('auth-expired', onAuthExpired as EventListener);

    return () => {
      window.removeEventListener('user-logged-in', userLoggedInListener as EventListener);
      window.removeEventListener('open-login', openLoginListener as EventListener);
      window.removeEventListener('auth-expired', onAuthExpired as EventListener);
    };
  }, [refreshStaffAccess]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const onVisible = () => {
      if (!document.hidden) void refreshStaffAccess();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isLoggedIn, refreshStaffAccess]);

  // Listener para abrir checkout
  useEffect(() => {
    const openCheckoutListener = () => {
      setHideCheckoutClose(true); // Esconder X quando vem de evento
      setIsCheckoutOpen(true);
    };
    window.addEventListener(
      'open-checkout',
      openCheckoutListener as EventListener
    );
    return () => {
      window.removeEventListener(
        'open-checkout',
        openCheckoutListener as EventListener
      );
    };
  }, []);

  // Contar eventos no carrinho
  useEffect(() => {
    const updateCartCount = async () => {
      try {
        // Contar eventos duplicados do localStorage (se houver)
        let eventosDuplicadosCount = 0;
        const duplicacaoAtiva = localStorage.getItem('duplicacao_ativa') === 'true';
        if (duplicacaoAtiva) {
          try {
            const eventosDuplicadosStr = localStorage.getItem('duplicacao_eventos');
            if (eventosDuplicadosStr) {
              const eventosDuplicados = JSON.parse(eventosDuplicadosStr);
              if (Array.isArray(eventosDuplicados)) {
                eventosDuplicadosCount = eventosDuplicados.length;
              }
            }
          } catch (e) {
            // Ignorar erro de parse
          }
        }

        const pedidoId = localStorage.getItem('pedido_id');
        if (!pedidoId) {
          // Se não há pedido_id, contar apenas eventos duplicados
          setCartEventCount(eventosDuplicadosCount);
          return;
        }

        // Buscar carrinhos primeiro (como o Checkout faz)
        // Depois buscar itens de cada carrinho para contar apenas carrinhos COM itens
        const { getJson } = await import('@/lib/apiConfig');
        
        // PASSO 1: Buscar TODOS os carrinhos (eventos) deste pedido
        const carrinhos = await getJson<any[]>('rEsr7KLt', 'carrinho', {
          pedido_id: parseInt(pedidoId),
        });

        if (!Array.isArray(carrinhos) || carrinhos.length === 0) {
          // Se não há carrinhos no backend, contar apenas eventos duplicados
          setCartEventCount(eventosDuplicadosCount);
          return;
        }

        // PASSO 2: Buscar itens de TODOS os carrinhos em paralelo
        const itensPorCarrinho = await Promise.all(
          carrinhos.map(async (carrinho: any) => {
            try {
              const items = await getJson<any[]>('rEsr7KLt', 'carrinho_produtos', {
                carrinho_id: carrinho.id,
                pedido_id: parseInt(pedidoId),
              });
              return {
                carrinhoId: carrinho.id,
                itens: Array.isArray(items) ? items : [],
              };
            } catch (error) {
              return { carrinhoId: carrinho.id, itens: [] };
            }
          })
        );

        // Contar apenas carrinhos que têm itens
        const carrinhosComItens = itensPorCarrinho.filter(
          (item) => item.itens && item.itens.length > 0
        );

        // Combinar contagem: eventos do backend + eventos duplicados
        const count = carrinhosComItens.length + eventosDuplicadosCount;
        setCartEventCount(count);
      } catch (error) {
        // Em caso de erro, tentar contar apenas eventos duplicados
        try {
          const duplicacaoAtiva = localStorage.getItem('duplicacao_ativa') === 'true';
          if (duplicacaoAtiva) {
            const eventosDuplicadosStr = localStorage.getItem('duplicacao_eventos');
            if (eventosDuplicadosStr) {
              const eventosDuplicados = JSON.parse(eventosDuplicadosStr);
              if (Array.isArray(eventosDuplicados)) {
                setCartEventCount(eventosDuplicados.length);
                return;
              }
            }
          }
        } catch (e) {
          // Ignorar erro
        }
        setCartEventCount(0);
      }
    };

    // Atualizar imediatamente ao montar
    updateCartCount();

    // Atualizar também após um pequeno delay para garantir que os dados foram carregados
    const initialTimer = setTimeout(() => {
      updateCartCount();
    }, 1000);

    // Escutar mudanças no localStorage (só funciona entre abas diferentes)
    const handleStorageChange = () => updateCartCount();
    window.addEventListener('storage', handleStorageChange);

    // Escutar eventos customizados de mudança no carrinho
    window.addEventListener('cart-updated', handleStorageChange);
    window.addEventListener('order-finalized', handleStorageChange);

    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cart-updated', handleStorageChange);
      window.removeEventListener('order-finalized', handleStorageChange);
    };
  }, []);

  // Atualizar contador quando a página fica visível novamente
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Quando a página fica visível, atualizar contador
        window.dispatchEvent(new Event('cart-updated'));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleUserButtonClick = async () => {
    if (isLoggedIn) {
      // Validar token antes de abrir perfil
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoggedIn(false);
        window.dispatchEvent(new Event('open-login'));
        return;
      }
      
      try {
        const { getJson } = await import('@/lib/apiConfig');
        await getJson<any>('9-ms6pAp', 'auth/me', undefined, {
          preserveSessionOn401: true,
        });
        setIsProfileOpen(true);
      } catch (error: unknown) {
        const message = String(
          error && typeof error === 'object' && 'message' in error
            ? (error as { message?: string }).message
            : ''
        );

        if (isInternalDisabledApiError(error)) {
          setIsProfileOpen(true);
          return;
        }

        if (
          message.includes('401') ||
          message.includes('403') ||
          message.includes('expired')
        ) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('user_id');
          localStorage.removeItem('wallet');
          setIsLoggedIn(false);
          window.dispatchEvent(new Event('open-login'));
        } else {
          setIsProfileOpen(true);
        }
      }
    } else {
      window.dispatchEvent(new Event('open-login'));
    }
  };

  const handleCloseLogin = () => {
    setIsLoginOpen(false);
  };

  const handleCloseRegister = () => {
    setIsRegisterOpen(false);
  };

  const handleCloseForgotPassword = () => {
    setIsForgotPasswordOpen(false);
  };

  const handleCloseProfile = () => {
    setIsProfileOpen(false);
  };

  const handleOpenRegister = () => {
    setIsLoginOpen(false);
    setIsRegisterOpen(true);
  };

  const handleOpenLogin = () => {
    setIsRegisterOpen(false);
    setIsForgotPasswordOpen(false);
    setIsLoginOpen(true);
  };

  const handleOpenForgotPassword = () => {
    setIsLoginOpen(false);
    setIsForgotPasswordOpen(true);
  };

  // Função para atualizar o estado de login após login bem-sucedido
  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    setIsLoginOpen(false);
    void refreshStaffAccess();
  };

  // Função para atualizar o estado após registro bem-sucedido
  const handleRegisterSuccess = () => {
    // Verificar se há token (login automático foi feito)
    const token = localStorage.getItem('authToken');
    if (token) {
      // Se houver token, usuário foi logado automaticamente
      setIsLoggedIn(true);
      setIsRegisterOpen(false);
      void refreshStaffAccess();
    } else {
      // Se não houver token, apenas fechar registro e abrir login
      setIsRegisterOpen(false);
      setIsLoginOpen(true);
    }
  };

  const handleBackofficeClick = async () => {
    try {
      const me = await fetchAuthMe();
      if (!isStaffUser(me)) return;
      navigate(getStaffEntryPath(me));
    } catch {
      setIsLoggedIn(false);
      setIsStaff(false);
      window.dispatchEvent(new Event('open-login'));
    }
  };

  return (
    <>
      <header className='bg-white shadow-md'>
        <div className='max-w-7xl mx-auto px-10 sm:px-6 lg:px-12 py-4'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center'>
              <Link to='/' className='flex items-center'>
                <img
                  src='/assets/img/logo.avif'
                  alt='Pão e Talho'
                  className='h-12 w-auto'
                />
              </Link>
            </div>
            <div className='flex items-center space-x-3'>
              <motion.button
                id='user-button'
                onClick={handleUserButtonClick}
                className='header-icon-btn'
                aria-label='Acessar conta'
                whileHover={{
                  scale: 1.05,
                  y: -2,
                }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 17,
                }}
              >
                <User size={20} />
              </motion.button>

              {isStaff && (
                <motion.button
                  id='backoffice-button'
                  onClick={() => void handleBackofficeClick()}
                  className='header-icon-btn'
                  aria-label='Abrir backoffice'
                  title='Backoffice'
                  whileHover={{
                    scale: 1.05,
                    y: -2,
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 17,
                  }}
                >
                  <Settings size={20} />
                </motion.button>
              )}

              <div
                id='cart-button'
                className='cart-button-wrapper'
                onClick={() => {
                  setHideCheckoutClose(false); // Mostrar X quando clica no ícone
                  setIsCheckoutOpen(true);
                }}
              >
                <motion.div
                  className='header-icon-btn cart-button-inner'
                  whileHover={{
                    scale: 1.05,
                    y: -2,
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 17,
                  }}
                >
                  <ShoppingCart size={20} />
                  {cartEventCount > 0 && (
                    <motion.span
                      className='cart-badge'
                      initial={{ scale: 0 }}
                      animate={{
                        scale: 1,
                        rotate: [0, 10, -10, 0],
                      }}
                      transition={{
                        scale: { type: 'spring', stiffness: 500, damping: 15 },
                        rotate: { duration: 0.5 },
                      }}
                      whileHover={{
                        scale: 1.1,
                        rotate: 5,
                      }}
                    >
                      {cartEventCount}
                    </motion.span>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {!mandatoryAuthBlocking && (
        <>
          <LoginModal
            isOpen={isLoginOpen}
            onClose={handleCloseLogin}
            onOpenRegister={handleOpenRegister}
            onOpenForgotPassword={handleOpenForgotPassword}
            onLoginSuccess={handleLoginSuccess}
          />

          <RegisterModal
            isOpen={isRegisterOpen}
            onClose={handleCloseRegister}
            onOpenLogin={handleOpenLogin}
            onRegisterSuccess={handleRegisterSuccess}
          />

          <ForgotPasswordModal
            isOpen={isForgotPasswordOpen}
            onClose={handleCloseForgotPassword}
            onBackToLogin={handleOpenLogin}
          />
        </>
      )}

      <ProfileModal isOpen={isProfileOpen} onClose={handleCloseProfile} />

      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => {
          setIsCheckoutOpen(false);
          setHideCheckoutClose(false); // Resetar flag ao fechar
          // Disparar evento para atualizar contador após fechar o modal
          setTimeout(() => {
            window.dispatchEvent(new Event('cart-updated'));
          }, 300);
        }}
        hideCloseButton={hideCheckoutClose}
      />
    </>
  );
};

export default Header;
